/**
 * Prioritization Context types
 * Mirrors the prioritization_contexts table schema from migration 282.
 * Implements the Context Invariant (P01) from Doc 01 §8.
 * See 10C-schema-design-configurations-and-context.md.
 */

export type ContextType = 'cycle' | 'group' | 'personal';
export type ContextStatus = 'active' | 'archived' | 'completed';

/** Full context record as returned from Supabase */
export interface PrioritizationContext {
  id: string;
  workspace_id: string;
  context_type: ContextType;
  cycle_id: string | null;
  group_id: string | null;
  owner_id: string | null;
  configuration_id: string | null;
  name: string | null;
  status: ContextStatus;
  created_at: string;
  created_by: string | null;
  updated_at: string;
}

/** Input for creating a new context — exactly one FK must be set */
export interface CreateContextParams {
  workspace_id: string;
  context_type: ContextType;
  cycle_id?: string;
  group_id?: string;
  owner_id?: string;
  configuration_id?: string;
  name?: string;
  actor_id?: string;
}

/** Response from evaluate_context_readiness RPC */
export interface ContextReadinessResult {
  valid: boolean;
  context_type: ContextType | null;
  missing_inputs: string[];
  blockers: string[];
  /** Present when the RPC returns per-stage data (migration 287+). If absent,
   *  the client falls back to deriveStageCompletionFromReadiness(missing_inputs). */
  stage_completion?: StageCompletionStatus;
}

/** Stage visibility state for the Next-Step Engine */
export interface StageCompletionStatus {
  participation: boolean;
  pool_formation: boolean;
  criteria_selection: boolean;
  criteria_expression: boolean;
  item_scoring: boolean;
  final_ranking: boolean;
}

/** Named stages in order — the engine uses this to surface the next incomplete stage */
export const STAGE_ORDER: (keyof StageCompletionStatus)[] = [
  'participation',
  'pool_formation',
  'criteria_selection',
  'criteria_expression',
  'item_scoring',
  'final_ranking',
];

export const STAGE_LABELS: Record<keyof StageCompletionStatus, string> = {
  participation: 'Participation',
  pool_formation: 'Pool Formation',
  criteria_selection: 'Criteria Selection',
  criteria_expression: 'Criteria Expression',
  item_scoring: 'Item Scoring',
  final_ranking: 'Final Ranking',
};

export const STAGE_DESCRIPTIONS: Record<keyof StageCompletionStatus, string> = {
  participation: 'Who is participating in this prioritization effort?',
  pool_formation: 'What items are in scope for evaluation?',
  criteria_selection: 'What criteria will guide the evaluation?',
  criteria_expression: 'How are criteria weighted and scaled?',
  item_scoring: 'Items scored against criteria by participants',
  final_ranking: 'Final ranked list produced and published',
};

/**
 * Returns the first incomplete stage in order.
 * This is the canonical "next step" for a context.
 */
export function getNextIncompleteStage(
  status: StageCompletionStatus
): keyof StageCompletionStatus | null {
  return STAGE_ORDER.find((stage) => !status[stage]) ?? null;
}
